import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useOptionalEntityPreview, isPreviewable, type PreviewOrigin } from '../contexts/EntityPreviewContext';
import type { EntityType } from './EntityChip';

interface Props {
  to: string;
  type: EntityType;
  id: string;
  className?: string;
  title?: string;
  /** Where the full page's back link should return to; also sent through the modal's Open. */
  state?: PreviewOrigin;
  children: ReactNode;
}

/**
 * A link that opens the entity preview modal on a plain click, so you stay
 * where you are (#267). Ctrl/Cmd/Shift/middle-click still act as a normal
 * link (new tab or window). Outside an EntityPreviewProvider, or for a type
 * the modal can't show, it just navigates.
 */
export function PreviewLink({ to, type, id, className, title, state, children }: Props) {
  const preview = useOptionalEntityPreview();

  function onClick(e: MouseEvent<HTMLAnchorElement>) {
    if (!preview || !isPreviewable(type)) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    preview.openPreview(type, id, undefined, state);
  }

  return (
    <Link to={to} state={state} onClick={onClick} className={className} title={title}>
      {children}
    </Link>
  );
}
